import { useState, useEffect } from 'react';

interface MobileDeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isPhone: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  touchSupport: boolean;
  userAgent: string;
}

export const useMobileDetection = (): MobileDeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<MobileDeviceInfo>({
    isMobile: false,
    isTablet: false,
    isPhone: false,
    screenWidth: 0,
    screenHeight: 0,
    orientation: 'portrait',
    touchSupport: false,
    userAgent: '',
  });

  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const userAgent = navigator.userAgent.toLowerCase();
      
      // Check for touch support
      const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Determine device type
      const isMobile = width <= 768 || /mobile|android|iphone|ipad|phone/i.test(userAgent);
      const isTablet = width > 768 && width <= 1024 || /tablet|ipad/i.test(userAgent);
      const isPhone = width <= 768 && !isTablet;
      
      // Determine orientation
      const orientation = width > height ? 'landscape' : 'portrait';

      setDeviceInfo({
        isMobile,
        isTablet,
        isPhone,
        screenWidth: width,
        screenHeight: height,
        orientation,
        touchSupport,
        userAgent,
      });
    };

    // Initial detection
    updateDeviceInfo();

    // Listen for resize and orientation changes
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);

    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};

export const useIsMobile = () => {
  const { isMobile } = useMobileDetection();
  return isMobile;
};

export const useIsTablet = () => {
  const { isTablet } = useMobileDetection();
  return isTablet;
};

export const useIsPhone = () => {
  const { isPhone } = useMobileDetection();
  return isPhone;
}; 